import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * When data-play-on-interaction is set: play on hover (desktop) or click (mobile).
 * Video loads only on hover/click - no load() in connectedCallback for performance.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [playButton] - Optional play button (when play-on-interaction).
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    if (this.hasAttribute('data-play-on-interaction')) {
      this.setupPlayOnInteraction();
      return;
    }

    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }
    videoElement.load();
  }

  setupPlayOnInteraction() {
    const { videoSources, videoElement } = this.refs;
    const playButton = this.querySelector('.video-background__play-button');
    const mediaWrapper = this.closest('.card__media-wrapper');
    const card = this.closest('.card');

    if (!videoElement) return;

    videoElement.pause();

    let sourcesAttached = false;

    const attachSources = () => {
      if (sourcesAttached) return;
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource) source.src = videoSource;
      }
      videoElement.load();
      sourcesAttached = true;
    };

    const play = () => {
      attachSources();
      document.querySelectorAll('video-background-component[data-play-on-interaction] video').forEach((v) => {
        if (v !== videoElement) v.pause();
      });
      videoElement.play().catch(() => {});
      playButton?.classList.add('is-hidden');
    };

    const pause = () => {
      videoElement.pause();
      videoElement.currentTime = 0;
      playButton?.classList.remove('is-hidden');
    };

    const toggle = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (videoElement.paused) {
        play();
      } else {
        pause();
      }
    };

    const hasHoverSupport = () =>
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const hoverTarget = mediaWrapper || card || this;

    if (hasHoverSupport()) {
      hoverTarget.addEventListener('mouseenter', play);
      hoverTarget.addEventListener('mouseleave', pause);
    } else {
      hoverTarget.addEventListener('click', toggle);
    }

    if (playButton) {
      playButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(e);
      });
    }

    videoElement.addEventListener('pause', () =>
      playButton?.classList.remove('is-hidden')
    );
    videoElement.addEventListener('play', () =>
      playButton?.classList.add('is-hidden')
    );

    /* Video bittiğinde loop: tekrar başlat */
    videoElement.addEventListener('ended', () => {
      videoElement.currentTime = 0;
      videoElement.play().catch(() => {});
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._isInViewport = true;
          } else {
            this._isInViewport = false;
          }
        });
      },
      { rootMargin: '300px', threshold: 0.1 }
    );
    observer.observe(this);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
